import { Analytics } from '@util/Analytics';
import { ConsentManager } from '@util/Consent';
import { Environment } from '@util/Environment';
import { Errors } from '@util/Errors';
import { Localization, translate } from '@util/Localization';
import { QueryParams } from '@util/QueryParams';
import React, { FunctionComponent, useEffect, useState } from 'react';
import 'react-app-polyfill/stable';
import ReactDOM from 'react-dom';
import { Helmet } from 'react-helmet';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Routes } from './Routes';
import theme from './Theme';
import { ConsentPreferences } from './components/ConsentPreferences';
import { AppLogos } from './components/assets/images/Logos';
import { DevelopmentToolbox } from './components/development/DevelopmentToolbox';
import { ErrorToolbox } from './components/development/ErrorToolbox';
import { Firebase } from './helpers/Firebase';
import { AppStoreProvider } from './store/AppStoreProvider';
// Ensure Stripe script tag is inserted immediately to enable "advanced fraud functionality" throughout the app
import '@stripe/stripe-js';
import { GeoLocate } from './helpers/GeoLocate';
import { stripeInitialize } from './helpers/Stripe';
import { initializeApp } from './helpers/initializeApp';

// Where possible, initialize modules independently of React for faster first render
Localization.initialize();
Firebase.initialize();
ConsentManager.initialize();
Analytics.initialize();
Errors.initialize();
QueryParams.initialize();
GeoLocate.initialize();
// Stripe recommends loading Stripe.js when the app starts for fraud detection. We do not know the
// customer's account yet, so preload the existing AU account here. Checkout loads the account returned
// by the backend before rendering payment fields.
stripeInitialize('au');

export const App: FunctionComponent = () => {
  const [initialized, setInitialized] = useState(false);

  useEffect(function initializeAppUponMount() {
    initializeApp().then(() => {
      setInitialized(true);
    });
  }, []);

  return (
    <AppStoreProvider initialized={initialized}>
      <ThemeProvider theme={theme}>
        <Helmet>
          <link rel="shortcut icon" href={AppLogos.favLogo} />
          <title>{translate('title')}</title>
          <link rel="apple-touch-icon" href={AppLogos.appleTouch} />
          <meta name="description" content={translate('description')} />
        </Helmet>
        {(Environment.DEVELOPMENT || Environment.STAGING) && (
          <>
            <DevelopmentToolbox />
            <ErrorToolbox />
          </>
        )}
        <Router>
          <Routes />
          <ConsentPreferences />
        </Router>
      </ThemeProvider>
    </AppStoreProvider>
  );
};

ReactDOM.render(<App />, document.getElementById('root'));
